import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePlanFeatures from '../hooks/usePlanFeatures';
import { Home, Users, Briefcase, FileText, PieChart, LogOut, TrendingUp, Calendar, Menu, X, Settings, Sparkles, ChevronDown, BarChart3, Globe, CreditCard, UserCog, Clock, Building2, ShieldCheck, Target, Link2, MessageSquare, Package, Heart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const IS_SAAS = process.env.REACT_APP_IS_SAAS === 'true';

const getDaysRemaining = (trialEndsAt) => {
  if (!trialEndsAt) return null;
  return Math.max(0, Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)));
};

const Navigation = () => {
  const { user, logout } = useAuth();
  const { isFeatureAllowed } = usePlanFeatures();
  const { t } = useTranslation('nav');
  const subscription = user?.subscription;
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close mobile menu when navigation occurs
  const handleNavigation = () => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  // Route değişinde dropdown'u kapat
  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);

  // Click outside → dropdown'u kapat
  useEffect(() => {
    if (!openDropdown) return undefined;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openDropdown]);

  // Esc ile dropdown'u kapat
  useEffect(() => {
    if (!openDropdown) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') setOpenDropdown(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [openDropdown]);

  // ── Platform Yönetimi (Süper Admin) ──────────────────────────────────────
  const superAdminMenuItems = [
    {
      path: '/admin',
      name: 'businesses',
      icon: Building2,
      description: 'businessesDesc',
    },
    {
      path: '/admin/leads',
      name: 'leads',
      icon: Target,
      description: 'leadsDesc',
    },
    {
      path: '/admin/funnel',
      name: 'funnel',
      icon: TrendingUp,
      description: 'funnelDesc',
    },
    {
      path: '/admin/campaigns',
      name: 'campaigns',
      icon: Link2,
      description: 'campaignsDesc',
    },
  ];

  const menuItems = [
    {
      path: '/home',
      name: 'home',
      icon: Home,
      allowedRoles: ['admin', 'cashier']
    },
    {
      path: '/appointments',
      name: 'appointments',
      icon: Calendar,
      allowedRoles: ['admin', 'cashier'],
      requiredFeature: 'appointments',
      isNew: true
    },
    {
      name: 'management',
      icon: Briefcase,
      allowedRoles: ['admin', 'cashier'],
      children: [
        {
          path: '/customers',
          name: 'customers',
          icon: Users,
          description: 'customersDesc',
        },
        {
          path: '/employee',
          name: 'employees',
          icon: Briefcase,
          description: 'employeesDesc',
          allowedRoles: ['admin'],
        },
        {
          path: '/services',
          name: 'services',
          icon: FileText,
          description: 'servicesDesc',
        },
      ],
    },
    {
      name: 'analytics',
      icon: BarChart3,
      allowedRoles: ['admin'],
      children: [
        {
          path: '/report',
          name: 'reports',
          icon: PieChart,
          description: 'reportsDesc',
        },
        {
          path: '/bests',
          name: 'statistics',
          icon: TrendingUp,
          description: 'statisticsDesc',
        },
        {
          path: '/commissions',
          name: 'commissions',
          icon: TrendingUp,
          description: 'commissionsDesc',
          requiredFeature: 'commissions',
        },
      ],
    },
    ...(IS_SAAS ? [{
      name: 'clientTools',
      icon: Heart,
      allowedRoles: ['admin'],
      children: [
        {
          path: '/loyalty-config',
          name: 'loyaltySettings',
          icon: Sparkles,
          description: 'loyaltySettingsDesc',
          requiredFeature: 'loyalty',
        },
        {
          path: '/business-profile',
          name: 'publicProfile',
          icon: Globe,
          description: 'publicProfileDesc',
          requiredFeature: 'appointments',
        },
        {
          path: '/sms-settings',
          name: 'smsSettings',
          icon: MessageSquare,
          description: 'smsSettingsDesc',
          requiredFeature: 'sms',
          comingSoon: true,
        },
      ],
    }] : []),
    {
      name: 'settings',
      icon: Settings,
      allowedRoles: ['admin'],
      children: [
        {
          path: '/customization',
          name: 'customization',
          icon: Settings,
          description: 'customizationDesc',
        },
        {
          path: '/team',
          name: 'teamManagement',
          icon: UserCog,
          description: 'teamManagementDesc',
        },
        ...(IS_SAAS ? [{
          path: '/settings',
          name: 'accountSettings',
          icon: UserCog,
          description: 'accountSettingsDesc',
        }] : []),
        ...(IS_SAAS ? [{
          path: '/billing',
          name: 'subscription',
          icon: CreditCard,
          description: 'subscriptionDesc',
        }] : []),
      ],
    },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Çıkış yapılırken hata oluştu:', error);
    }
  };

  const renderNavItems = (mobile = false) => {
    // Süper admin: sadece platform yönetim menüsü
    if (user?.superadmin) {
      return superAdminMenuItems.map((item) => (
        <NavLink
          key={`${item.path}${mobile ? '-mobile' : ''}`}
          to={item.path}
          onClick={handleNavigation}
          className={({ isActive }) =>
            `px-3 py-2 text-sm font-medium rounded-md transition-all duration-300
            flex items-center space-x-1
            ${isActive
              ? 'text-blue-600 bg-blue-50'
              : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
            } ${mobile ? 'w-full' : ''}`
          }
        >
          <item.icon className="h-4 w-4" />
          <span>{t(item.name)}</span>
        </NavLink>
      ));
    }

    return menuItems.map((item) => {
      if (!item.allowedRoles.includes(user?.role)) return null;
      if (item.requiredFeature && !isFeatureAllowed(item.requiredFeature)) return null;

      // === Dropdown (children olan öğeler) ===
      if (item.children && item.children.length > 0) {
        const visibleChildren = item.children.filter(
          (c) =>
            (!c.allowedRoles || c.allowedRoles.includes(user?.role)) &&
            (!c.requiredFeature || isFeatureAllowed(c.requiredFeature)),
        );
        if (visibleChildren.length === 0) return null;

        const isAnyChildActive = visibleChildren.some(
          (c) => location.pathname === c.path,
        );
        const isOpen = openDropdown === item.name;

        // Mobile: flat section header + nested items (her zaman açık)
        if (mobile) {
          return (
            <div key={`${item.name}-mobile-group`} className="pt-2">
              <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                <item.icon className="h-3 w-3" />
                {t(item.name)}
              </div>
              <div className="mt-1 space-y-1">
                {visibleChildren.map((child) =>
                  child.comingSoon ? (
                    <div
                      key={`${child.path}-mobile`}
                      className="pl-7 pr-3 py-2 text-sm font-medium rounded-md flex items-center gap-2 w-full text-gray-400 cursor-not-allowed select-none"
                    >
                      <child.icon className="h-4 w-4 opacity-50" />
                      <span>{t(child.name)}</span>
                      <span className="text-[10px] font-semibold bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full leading-none ml-1">
                        Yakında
                      </span>
                    </div>
                  ) : (
                  <NavLink
                    key={`${child.path}-mobile`}
                    to={child.path}
                    onClick={handleNavigation}
                    className={({ isActive }) =>
                      `pl-7 pr-3 py-2 text-sm font-medium rounded-md transition-all duration-300 flex items-center gap-2 w-full ${
                        isActive
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                      }`
                    }
                  >
                    <child.icon className="h-4 w-4" />
                    <span>{t(child.name)}</span>
                  </NavLink>
                  )
                )}
              </div>
            </div>
          );
        }

        // Desktop: Dropdown button + panel
        return (
          <div
            key={item.name}
            className="relative"
            ref={isOpen ? dropdownRef : null}
          >
            <button
              type="button"
              onClick={() => setOpenDropdown(isOpen ? null : item.name)}
              aria-expanded={isOpen}
              aria-haspopup="menu"
              className={`px-3 py-2 text-sm font-medium rounded-md transition-all duration-300 flex items-center gap-1 ${
                isAnyChildActive || isOpen
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <item.icon className="h-4 w-4" />
              <span>{t(item.name)}</span>
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl py-1.5 z-50 origin-top-right"
                style={{ animation: 'dropdownOpen 150ms ease-out' }}
              >
                {visibleChildren.map((child) =>
                  child.comingSoon ? (
                    <div
                      key={child.path}
                      role="menuitem"
                      className="flex items-start gap-2.5 px-3 py-2 text-sm text-gray-400 cursor-not-allowed select-none"
                    >
                      <child.icon className="h-4 w-4 flex-shrink-0 mt-0.5 opacity-50" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 font-medium">
                          {t(child.name)}
                          <span className="text-[10px] font-semibold bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full leading-none">
                            Yakında
                          </span>
                        </div>
                        {child.description && (
                          <div className="text-[11px] text-gray-400 mt-0.5">
                            {t(child.description)}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                  <NavLink
                    key={child.path}
                    to={child.path}
                    role="menuitem"
                    onClick={handleNavigation}
                    className={({ isActive }) =>
                      `flex items-start gap-2.5 px-3 py-2 text-sm transition-colors ${
                        isActive
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`
                    }
                  >
                    <child.icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{t(child.name)}</div>
                      {child.description && (
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {t(child.description)}
                        </div>
                      )}
                    </div>
                  </NavLink>
                  )
                )}
              </div>
            )}
          </div>
        );
      }

      // === Normal NavLink (children'ı olmayan öğeler) ===
      return (
        <NavLink
          key={`${item.path}${mobile ? '-mobile' : ''}`}
          to={item.path}
          onClick={handleNavigation}
          className={({ isActive }) =>
            `px-3 py-2 text-sm font-medium rounded-md transition-all duration-300
            flex items-center space-x-1 relative
            ${isActive 
              ? 'text-blue-600 bg-blue-50' 
              : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
            } ${mobile ? 'w-full' : ''}`
          }
        >
          <item.icon className="h-4 w-4" />
          <span>{t(item.name)}</span>
          {item.isNew && (
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
            </span>
          )}
        </NavLink>
      );
    });
  };

  const daysRemaining = getDaysRemaining(subscription?.trialEndsAt);
  const showTrialBanner = IS_SAAS && subscription?.status === 'trialing' && daysRemaining !== null;
  const bannerColor = daysRemaining <= 3
    ? 'bg-red-600'
    : daysRemaining <= 10
    ? 'bg-yellow-500'
    : 'bg-blue-600';

  return (
    <div className="bg-gradient-to-r from-cyan-500 to-blue-500 pb-1">
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo Area */}
            <div className="flex-shrink-0 flex items-center gap-2">
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Adisyon Takip
              </span>
              {user?.superadmin && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[11px] font-semibold rounded-full">
                  <ShieldCheck className="h-3 w-3" />
                  {t('platformAdmin')}
                </span>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="sm:hidden">
              <button 
                onClick={toggleMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 
                hover:text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <span className="sr-only">Open main menu</span>
                {isMenuOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>

            {/* Desktop Navigation Items */}
            <div className="hidden sm:flex sm:space-x-4">
              {renderNavItems()}
            </div>

            {/* Dil Seçici */}
            <div className="hidden sm:flex items-center">
              <LanguageSwitcher />
            </div>

            {/* Desktop Logout Button */}
            <div className="hidden sm:block">
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 
                         hover:text-red-600 hover:bg-red-50 rounded-md transition-all duration-300"
              >
                <LogOut className="h-4 w-4 mr-1" />
                <span>{t('logout')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="sm:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white shadow-lg border-t">
              {/* Mobile Navigation Items */}
              <div className="flex flex-col space-y-1">
                {renderNavItems(true)}
              </div>
              
              {/* Dil Seçici - Mobile */}
              <div className="flex justify-end px-3 py-1 border-t border-gray-100 mt-1">
                <LanguageSwitcher />
              </div>

              {/* Mobile Logout Button */}
              <button
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}
                className="w-full flex items-center justify-start px-3 py-2 text-sm font-medium text-gray-600 
                         hover:text-red-600 hover:bg-red-50 rounded-md transition-all duration-300 mt-2"
              >
                <LogOut className="h-4 w-4 mr-1" />
                <span>{t('logout')}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Trial Banner */}
      {showTrialBanner && (
        <div className={`border-b ${
          daysRemaining <= 3
            ? 'bg-red-50 border-red-100'
            : daysRemaining <= 10
            ? 'bg-amber-50 border-amber-100'
            : 'bg-blue-50 border-blue-100'
        }`}>
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className={`h-3.5 w-3.5 flex-shrink-0 ${
                daysRemaining <= 3 ? 'text-red-500' : daysRemaining <= 10 ? 'text-amber-500' : 'text-blue-500'
              }`} />
              <span className={`text-xs font-medium ${
                daysRemaining <= 3 ? 'text-red-700' : daysRemaining <= 10 ? 'text-amber-700' : 'text-blue-700'
              }`}>
                {daysRemaining === 0
                  ? t('trialEndsToday')
                  : <>
                      {t('trialEnding')}
                      <span className="mx-1 opacity-40">·</span>
                      <span className="font-semibold">{t('daysLeft', { count: daysRemaining })}</span>
                    </>}
              </span>
            </div>
            <NavLink
              to="/billing"
              className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
                daysRemaining <= 3
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : daysRemaining <= 10
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {t('startSubscription')}
            </NavLink>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navigation;